import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import TelerehabilitationProgramModal from './TelerehabilitationProgramModal';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { TelerehabilitationProgramDto } from '../../../../../shared/dtos/telerehabilitationProgram.dto';
import DataTable, { DataTableColumn } from '../../../components/data-table/DataTable';
import PrimaryButton from '../../../components/PrimaryButton';

const TelerehabilitationPrograms: () => ReactElement = () => {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<TelerehabilitationProgramDto[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editTelerehabilitationProgram, setEditTelerehabilitationProgram] = useState<TelerehabilitationProgramDto | null>(null);
    

  const fetchTelerehabilitationPrograms: () => Promise<void> = useCallback(async () => {
    setIsLoading(true);

    try {
      const result: TelerehabilitationProgramDto[] = await authFetch('/api/v1/telerehabilitation-program');
      setRows(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchTelerehabilitationPrograms();
  }, [fetchTelerehabilitationPrograms]);
    
  
  const openEditModal = (program: TelerehabilitationProgramDto): void => {
    setEditTelerehabilitationProgram(program);
    setShowModal(true);
  };

  const columns: DataTableColumn<TelerehabilitationProgramDto>[] = [
    {
      key: 'germanName',
      header: i18next.t('adminTelerehabilitationProgram.germanName'),
      cell: (program: TelerehabilitationProgramDto) => <span className="fw-semibold">{program.germanName}</span>,
    },
    {
      key: 'englishName',
      header: i18next.t('adminTelerehabilitationProgram.englishName'),
      cell: (program: TelerehabilitationProgramDto) => <span className="text-muted">{program.englishName}</span>,
    },
    {
      key: 'targetTrainingMinutes',
      header: i18next.t('adminTelerehabilitationProgram.targetTrainingMinutes'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => program.targetTrainingMinutes,
    },
    {
      key: 'maximumBillableTherapyUnits',
      header: i18next.t('adminTelerehabilitationProgram.maximumBillableTherapyUnits'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => `${program.maximumBillableTherapyUnits} TE`,
    },
    {
      key: 'maximumBillableTeleTherapyUnits',
      header: i18next.t('adminTelerehabilitationProgram.maximumBillableTeleTherapyUnits'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => `${program.maximumBillableTeleTherapyUnits} TE`,
    },
    {
      key: 'maximumEnduranceTrainingPercentage',
      header: i18next.t('adminTelerehabilitationProgram.maximumEnduranceTrainingPercentage'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => `${program.maximumEnduranceTrainingPercentage}%`,
    },
    {
      key: 'basicPackage',
      header: i18next.t('adminTelerehabilitationProgram.basicPackage'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => `${program.basicPackage} TE`,
    },
    {
      key: 'teConversion',
      header: i18next.t('adminTelerehabilitationProgram.teConversion'),
      align: 'end',
      cell: (program: TelerehabilitationProgramDto) => program.teConversion,
    },
  ];

  return (
    <div className="pt-3">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(program: TelerehabilitationProgramDto) => program.id}
        onRowClick={openEditModal}
        isLoading={isLoading}
        toolbar={(
          <PrimaryButton
            className="ms-auto"
            onClick={() => {
              setEditTelerehabilitationProgram(null);
              setShowModal(true);
            }}
            disabled={isLoading}>
            {i18next.t('generic.create')}
          </PrimaryButton>
        )}
      />

      <TelerehabilitationProgramModal
        show={showModal}
        telerehabilitationProgram={editTelerehabilitationProgram}
        onClose={() => {
          setShowModal(false);
          setEditTelerehabilitationProgram(null);
        }}
        onSave={() => {
          void fetchTelerehabilitationPrograms();
        }}
      />
    </div>
  );
};
export default TelerehabilitationPrograms;
